import {useEffect, useState, type RefObject} from 'react';

/** A pupil offset toward the pointer, in the caller's SVG viewBox units. */
export interface Gaze {
  /** Horizontal offset; positive looks right. */
  dx: number;
  /** Vertical offset; positive looks down. */
  dy: number;
}

/** Pointer distance, in px, at which the gaze reaches its full travel. */
const FULL_TRAVEL_DISTANCE_PX = 200;

/**
 * Tracks the pointer anywhere on the page and returns how far a mascot's
 * pupils should shift to look at it. The offset grows with distance up to
 * `maxTravel`, so a nearby pointer gets a partial glance rather than a snap
 * to the rim.
 *
 * @param params.targetRef - Element whose box the gaze originates from.
 * @param params.maxTravel - Largest offset, in viewBox units.
 * @param params.eyeLineFraction - Height of the eyes within the target's box,
 *     as a fraction from its top. Omitted defaults to `0.5` (vertical center).
 * @return The current gaze offset; `{dx: 0, dy: 0}` until the pointer moves.
 * @throws Never.
 */
export function usePointerGaze({
  targetRef,
  maxTravel,
  eyeLineFraction = 0.5,
}: {
  targetRef: RefObject<Element | null>;
  maxTravel: number;
  eyeLineFraction?: number;
}): Gaze {
  const [gaze, setGaze] = useState<Gaze>({dx: 0, dy: 0});

  useEffect(() => {
    let frameId = 0;
    const onPointerMove = (event: PointerEvent) => {
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => {
        const target = targetRef.current;
        if (target === null) {
          return;
        }
        const rect = target.getBoundingClientRect();
        const dx = event.clientX - (rect.left + rect.width / 2);
        const dy = event.clientY - (rect.top + rect.height * eyeLineFraction);
        const distance = Math.hypot(dx, dy);
        if (distance === 0) {
          setGaze({dx: 0, dy: 0});
          return;
        }
        const reach =
          maxTravel * Math.min(1, distance / FULL_TRAVEL_DISTANCE_PX);
        setGaze({dx: (dx / distance) * reach, dy: (dy / distance) * reach});
      });
    };
    window.addEventListener('pointermove', onPointerMove);
    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('pointermove', onPointerMove);
    };
  }, [targetRef, maxTravel, eyeLineFraction]);

  return gaze;
}
